import * as React from 'react';
import { Table, TableHeaderRow, TableColumn, TableRow, FilterOperation } from '@devexpress/dx-react-grid';
import { GetSelectedFilterOperationFn, ClearFilterStateFns, ClearFilterStateFn, OnFiltersChange, HandleClearFilters } from './types';
import { ArcherGridFilter } from '../../Grid.types';

export const DEFAULT_FILTER_OPERATIONS = ['contains', 'notContains', 'startsWith', 'endsWith', 'equal', 'notEqual'];

export const isTableHeadingCell = (tableColumn: TableColumn, tableRow: TableRow) =>
  tableColumn.type === Table.COLUMN_TYPE && tableRow.type === TableHeaderRow.ROW_TYPE;

export const isFilterValueEmpty = (value: any) => value === undefined || !String(value).length;

export const isFilterOperationAnEmptyCheck = (operation: string | undefined) => operation === 'empty' || operation === 'notEmpty';

export const isFilterValid = (filter: ArcherGridFilter | null) =>
  filter !== null && (!isFilterValueEmpty(filter.value) || isFilterOperationAnEmptyCheck(filter.operation));

export const getColumnFilterConfig = (filters: ArcherGridFilter[], columnName: string) =>
  (filters?.length && filters.filter(filter => filter.columnName === columnName)[0]) || null;

export const getColumnFilterOperations = (getAvailableFilterOperations: (columnName: string) => FilterOperation[], columnName: string) =>
  (getAvailableFilterOperations && getAvailableFilterOperations(columnName)) || DEFAULT_FILTER_OPERATIONS;

export const getSelectedFilterOperation: GetSelectedFilterOperationFn = (columnFilter, columnFilterOperations) => {
  if (columnFilter && columnFilter.operation) {
    return columnFilter.operation;
  }
  return columnFilterOperations[0];
};

/**
 * When each column filter renders it adds a function to the clearFilterStateFns object that can be used to clear that React
 * component's internal state. These functions are invoked when the clearFilters button is pressed and all filters are removed.
 */
export const useManageClearFilterStateFns = () => {
  const clearFilterStateFns = React.useRef<ClearFilterStateFns>({});

  const addClearFilterStateFn = React.useCallback((columnName: string, filterStateFn: ClearFilterStateFn) => {
    clearFilterStateFns.current[columnName] = filterStateFn;
  }, []);

  const removeClearFilterStateFn = React.useCallback((columnName: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [columnName]: filterStateFnToRemove, ...remainingFilterStateFns } = clearFilterStateFns.current;
    clearFilterStateFns.current = remainingFilterStateFns;
  }, []);

  return { clearFilterStateFns, addClearFilterStateFn, removeClearFilterStateFn };
};

/**
 * This hook creates a function for the clearFilters button that clears both the grid's internal filter state and each column
 * filter component's internal state. It also wraps the grid's onFiltersChange prop so that if the clearFilters button is pressed
 * it only sends a notification to the parent component once at the end instead of once for each filter that is cleared.
 */
export const useManageFilterState = (onFiltersChange?: OnFiltersChange) => {
  const { clearFilterStateFns, addClearFilterStateFn, removeClearFilterStateFn } = useManageClearFilterStateFns();
  const wasClearFiltersPressed = React.useRef<boolean>(false);
  const setWasClearFiltersPressed = React.useCallback(
    (wasPressed: boolean) => {
      wasClearFiltersPressed.current = wasPressed;
    },
    [wasClearFiltersPressed]
  );

  const handleClearFilters: HandleClearFilters = React.useCallback(
    (filters, changeColumnFilter) => {
      setWasClearFiltersPressed(true);
      filters.forEach(filter => {
        const { columnName } = filter;
        if (clearFilterStateFns.current[columnName]) {
          clearFilterStateFns.current[columnName]();
        }
        changeColumnFilter({ columnName, config: null });
      });
    },
    [wasClearFiltersPressed, setWasClearFiltersPressed]
  );

  const notifyFiltersChanged: OnFiltersChange = React.useCallback(
    filters => {
      if (onFiltersChange && (!wasClearFiltersPressed.current || !filters?.length)) {
        setWasClearFiltersPressed(false);
        onFiltersChange(filters);
      }
    },
    [onFiltersChange, wasClearFiltersPressed, setWasClearFiltersPressed]
  );

  return { addClearFilterStateFn, removeClearFilterStateFn, handleClearFilters, notifyFiltersChanged };
};

export const isEmptyObject = (value: any) => {
  return value && value.constructor === Object && Object.keys(value).length === 0;
};
